import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import bacService from '../../services/bacStudent.service';
import type { BacStats, BacSujet } from '../../services/bacStudent.service';
import SegmentBar from './SegmentBar';
import { CHAPITRE_COLORS } from './chapitreColors';
import type { Segment } from './SegmentBar';

type Vue = 'annee' | 'sujet';
const fmt = (x: number) => x.toLocaleString('fr-FR', { maximumFractionDigits: 2 });

/** Points acquis / total d'un sujet, restreints aux chapitres sélectionnés. */
function scoreSujet(s: BacSujet, chaps: Set<string>) {
  let got = 0;
  let tot = 0;
  for (const q of s.questions) {
    if (!chaps.has(q.chapitre)) continue;
    tot += q.points;
    if (q.ok) got += q.points;
  }
  return { got, tot };
}

/** Segments d'un sujet : une question par segment, dans l'ordre du sujet. */
function segmentsSujet(s: BacSujet, chaps: Set<string>, titres: Record<string, string>): Segment[] {
  return s.questions.map((q) => ({
    points: q.points,
    ratio: q.ok ? 1 : 0,
    chapitre: q.chapitre,
    off: !chaps.has(q.chapitre),
    tooltip: `Ex. ${q.exercice} – ${q.ref} · ${fmt(q.points)} pt · ${titres[q.chapitre] || q.chapitre} · ${
      q.ok ? 'acquis' : 'non acquis'
    }`,
  }));
}

/** Moyenne d'un ensemble de sujets : un segment par chapitre (points moyens), rempli à hauteur des points acquis moyens. */
function moyenne(sujets: BacSujet[], chaps: Set<string>, ordre: string[], titres: Record<string, string>) {
  const n = sujets.length || 1;
  const tot: Record<string, number> = {};
  const got: Record<string, number> = {};
  for (const s of sujets) {
    for (const q of s.questions) {
      tot[q.chapitre] = (tot[q.chapitre] || 0) + q.points;
      if (q.ok) got[q.chapitre] = (got[q.chapitre] || 0) + q.points;
    }
  }
  let total = 0;
  let gotSel = 0;
  let totSel = 0;
  const segments: Segment[] = ordre
    .filter((c) => tot[c])
    .map((c) => {
      const t = tot[c] / n;
      const g = (got[c] || 0) / n;
      total += t;
      if (chaps.has(c)) {
        gotSel += g;
        totSel += t;
      }
      return {
        points: t,
        ratio: t ? g / t : 0,
        chapitre: c,
        off: !chaps.has(c),
        tooltip: `${titres[c] || c} · ${fmt(g)} / ${fmt(t)} pts en moyenne`,
      };
    });
  return { segments, total, got: gotSel, totSel };
}

export default function SujetsTab({ studentId }: { studentId: number }) {
  const [stats, setStats] = useState<BacStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [vue, setVue] = useState<Vue>('annee');
  const [annees, setAnnees] = useState<Set<number>>(new Set());
  const [chaps, setChaps] = useState<Set<string>>(new Set());

  useEffect(() => {
    bacService
      .getStats(studentId)
      .then((d) => {
        setStats(d);
        setAnnees(new Set(d.sujets.map((s) => s.annee)));
        setChaps(new Set(d.chapitres.map((c) => c.key)));
      })
      .catch(() => setError('Erreur lors du chargement des sujets'));
  }, [studentId]);

  const titres = useMemo(() => {
    const t: Record<string, string> = {};
    stats?.chapitres.forEach((c) => (t[c.key] = c.title));
    return t;
  }, [stats]);
  const ordre = useMemo(() => stats?.chapitres.map((c) => c.key) || [], [stats]);
  const toutesAnnees = useMemo(() => Array.from(new Set(stats?.sujets.map((s) => s.annee) || [])).sort(), [stats]);
  const sujets = useMemo(() => (stats?.sujets || []).filter((s) => annees.has(s.annee)), [stats, annees]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!stats) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  const toggleSet = <T,>(set: Set<T>, v: T, setter: (s: Set<T>) => void) => {
    const n = new Set(set);
    if (n.has(v)) n.delete(v);
    else n.add(v);
    setter(n);
  };

  const moy = moyenne(sujets, chaps, ordre, titres);

  return (
    <Box>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={vue}
        onChange={(_, v) => v && setVue(v)}
        sx={{ mb: 1.5 }}
      >
        <ToggleButton value="annee">Vue par année</ToggleButton>
        <ToggleButton value="sujet">Vue par sujet</ToggleButton>
      </ToggleButtonGroup>

      <FormGroup row sx={{ mb: 0.5 }}>
        {toutesAnnees.map((a) => (
          <FormControlLabel
            key={a}
            control={<Checkbox size="small" checked={annees.has(a)} onChange={() => toggleSet(annees, a, setAnnees)} />}
            label={String(a)}
          />
        ))}
      </FormGroup>

      {/* Les cases des chapitres servent aussi de légende (couleur + nom). */}
      <FormGroup row sx={{ mb: 2 }}>
        {stats.chapitres.map((c) => (
          <FormControlLabel
            key={c.key}
            control={
              <Checkbox
                size="small"
                checked={chaps.has(c.key)}
                onChange={() => toggleSet(chaps, c.key, setChaps)}
                sx={{ color: CHAPITRE_COLORS[c.key], '&.Mui-checked': { color: CHAPITRE_COLORS[c.key] } }}
              />
            }
            label={<Typography variant="body2">{c.title}</Typography>}
          />
        ))}
      </FormGroup>

      {sujets.length === 0 ? (
        <Typography color="text.secondary">Choisissez au moins une année.</Typography>
      ) : (
        <>
          <SegmentBar
            title="Moyenne"
            subtitle={`${sujets.length} sujet${sujets.length > 1 ? 's' : ''} · points moyens par chapitre`}
            segments={moy.segments}
            total={moy.total}
            got={moy.got}
            totalSel={moy.totSel}
          />

          {vue === 'annee'
            ? toutesAnnees
                .filter((a) => annees.has(a))
                .map((a) => {
                  const m = moyenne(
                    sujets.filter((s) => s.annee === a),
                    chaps,
                    ordre,
                    titres
                  );
                  const n = sujets.filter((s) => s.annee === a).length;
                  return (
                    <SegmentBar
                      key={a}
                      title={`Bac ${a}`}
                      subtitle={`${n} sujet${n > 1 ? 's' : ''} · points moyens par chapitre`}
                      segments={m.segments}
                      total={m.total}
                      got={m.got}
                      totalSel={m.totSel}
                    />
                  );
                })
            : sujets.map((s) => {
                const sc = scoreSujet(s, chaps);
                return (
                  <SegmentBar
                    key={s.id}
                    title={s.title}
                    subtitle={`${s.questions.length} questions · sujet sur ${fmt(s.total)}`}
                    segments={segmentsSujet(s, chaps, titres)}
                    total={s.total}
                    got={sc.got}
                    totalSel={sc.tot}
                  />
                );
              })}
        </>
      )}
    </Box>
  );
}
